import React from "react";
import { Badge } from "./badge";
import { Button } from "./button";
import { Card } from "./card";
import { Book } from "lucide-react";

export interface Course {
  title: string;
  status: "NEW" | "COMPLETED" | "REVIEW" | "PENDING";
  imageUrl: string;
  lessonsCompleted: number;
  totalLessons: number;
  unitsCompleted: number;
  totalUnits: number;
  submissionsCount: number;
  feedbacksCount: number;
  completionPercent: number;
  onViewCourse: () => void;
}

interface CourseCardProps {
  course: Course;
}

// Helper components to follow DRY principles
interface CheckboxItemProps {
  label: string;
  value: string;
}

const CheckboxItem: React.FC<CheckboxItemProps> = ({ label, value }) => (
  <div className="flex items-center">
    <Book className={`h-[18px] w-[18px] text-[#929292] mr-2`} />
    <span className="text-[#1c1a4a] text-[16px] font-medium">
      {label} {value}
    </span>
  </div>
);

interface CircularProgressProps {
  percent: number;
  color: string;
  label: string;
  value: string;
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  percent,
  color,
  label,
  value,
}) => {
  // Calculate the circumference of the circle
  const radius = 16;
  const circumference = 2 * Math.PI * radius;

  // Calculate the dash array and offset based on percentage
  const calculatedPercent = percent === 100 ? 99.9999 : percent; // Fix for 100% display
  const strokeDasharray = `${
    (calculatedPercent / 100) * circumference
  } ${circumference}`;

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-[68.5px] w-[68.5px]">
        <svg className="h-full w-full" viewBox="0 0 36 36">
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            className="stroke-gray-200"
            strokeWidth="3"
          />
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            className={color}
            strokeWidth="3"
            strokeDasharray={strokeDasharray}
            strokeDashoffset="0"
            strokeLinecap="round"
            transform="rotate(-90 18 18)"
          />
        </svg>
        <span className="text-[16px] font-[400] block text-center text-[#0A1929D9] mt-[10px]">
          {label}
        </span>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[16px] font-bold text-[#0A1929]">{value}</span>
        </div>
      </div>
    </div>
  );
};

const CourseCard: React.FC<CourseCardProps> = ({ course }) => {
  // Helper function to determine badge color based on status
  const getBadgeVariant = (status: Course["status"]) => {
    switch (status) {
      case "NEW":
        return "primary";
      case "COMPLETED":
        return "success";
      case "REVIEW":
        return "warning";
      case "PENDING":
        return "danger";
      default:
        return "primary";
    }
  };

  // Calculate percentage for circular progress indicators
  const lessonsPercent = Math.round(
    (course.lessonsCompleted / course.totalLessons) * 100
  );
  const unitsPercent = Math.round(
    (course.unitsCompleted / course.totalUnits) * 100
  );

  return (
    <div
      className="flex xl:flex-row flex-col justify-between border border-[#E7EAEC] rounded-[12px] p-[16px]"
      style={{
        background:
          "linear-gradient(103.76deg, rgba(255, 167, 69, 0.1) 9.84%, rgba(254, 134, 159, 0.1) 29.92%, rgba(239, 122, 200, 0.1) 50%, rgba(160, 131, 237, 0.1) 70.08%, rgba(67, 174, 255, 0.1) 90.16%)",
      }}
    >
      <div className="flex md:flex-row flex-col">
        <div className="w-[284px] h-[152px] rounded-[8px]">
          <img
            src={course.imageUrl}
            alt={`${course.title} thumbnail`}
            className="w-full h-full rounded-[8px]"
          />
        </div>

        <div className="md:px-[16px] px-0 md:pt-[10px] pt-[16px]">
          <h3
            className="text-[#161439] text-[18px] font-semibold"
            style={{ lineHeight: "25px" }}
          >
            {course.title}
          </h3>
          <Badge
            variant={getBadgeVariant(course.status)}
            className="py-[8px] px-[10px] h-[32px] min-w-[130px] mt-[8px] mb-[10px] rounded-[70px] font-medium text-[#1F1F1F]"
          >
            {course.status}
          </Badge>
          {/* Checkboxes for Lessons and Units in 2x2 grid */}
          <div className="grid grid-cols-2 gap-3 mt-2">
            <CheckboxItem
              label="Lessons"
              value={`${course.lessonsCompleted}/${course.totalLessons}`}
            />
            <CheckboxItem
              label="Units"
              value={`${course.unitsCompleted}/${course.totalUnits}`}
            />
            <CheckboxItem
              label="Submissions"
              value={`${course.submissionsCount}`}
            />
            <CheckboxItem
              label="Feedbacks"
              value={`${course.feedbacksCount}`}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center md:flex-row flex-col xl:my-0 md:my-[20px] mt-[30px]">
        <div className="flex justify-between gap-x-[20px] items-center md:mr-[80px] mb-4">
          <CircularProgress
            percent={lessonsPercent}
            color="stroke-[#FD7E14]"
            label="Lessons"
            value={`${course.lessonsCompleted}/${course.totalLessons}`}
          />
          <div className="w-[1px] h-[32px] bg-[#c4c4c4]"></div>
          {/* Units Progress */}
          <CircularProgress
            percent={unitsPercent}
            color="stroke-[#D6588B]"
            label="Units"
            value={`${course.unitsCompleted}/${course.totalUnits}`}
          />
          <div className="w-[1px] h-[32px] bg-[#c4c4c4]"></div>
          {/* Overall Completion */}
          <CircularProgress
            percent={course.completionPercent}
            color="stroke-[#5C88C5]"
            label="Completion"
            value={`${course.completionPercent}%`}
          />
        </div>
        <button
          onClick={course.onViewCourse}
          className="bg-[#5C88C5] md:mt-0 mt-[30px] w-[161px] h-[40px] text-white px-[12px] py-[8px] text-[16px] font-[600] rounded-[12px]"
        >
          View Course
        </button>
      </div>
    </div>
  );
};

export default CourseCard;
